import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import {
  getTableConfiguration,
  saveTableConfiguration,
} from '../../components/shared/PFPrime/services/table.configuration.service';

const initialState = {
  /** configs by tableKey: { [tableKey]: { fixHeader, frozenLeft } } */
  configs: {},
  status: false,
  error: null,
  saveLoading: false,
  saveError: null,
};

export const fetchTableConfiguration = createAsyncThunk(
  'tableConfiguration/fetch',
  async (tableKey, { getState, rejectWithValue }) => {
    const { tableConfiguration } = getState();
    if (
      tableConfiguration?.configs &&
      tableConfiguration.configs[tableKey] !== undefined
    ) {
      return rejectWithValue('table configuration already fetched');
    }
    const res = await getTableConfiguration(tableKey);
    const configStr =
      res?.data?.column_configuration ?? res?.column_configuration;
    if (!res || !configStr) {
      return rejectWithValue(null);
    }
    try {
      const config = JSON.parse(configStr);
      const frozenLeft = Array.isArray(config.frozenLeft ?? config.freezeleft)
        ? (config.frozenLeft ?? config.freezeleft)
        : [];
      const fixHeader =
        typeof (config.fixHeader ?? config.fixheader) === 'boolean'
          ? (config.fixHeader ?? config.fixheader)
          : false;
      return { tableKey, fixHeader, frozenLeft };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const saveTableConfigurationThunk = createAsyncThunk(
  'tableConfiguration/save',
  async (payload, { rejectWithValue }) => {
    const res = await saveTableConfiguration(payload);
    if (res && res.error) {
      return rejectWithValue(res.message || 'Save failed');
    }
    try {
      const config = payload.column_configuration
        ? JSON.parse(payload.column_configuration)
        : {};
      const frozenLeft = Array.isArray(config.frozenLeft ?? config.freezeleft)
        ? (config.frozenLeft ?? config.freezeleft)
        : [];
      const fixHeader =
        typeof (config.fixHeader ?? config.fixheader) === 'boolean'
          ? (config.fixHeader ?? config.fixheader)
          : false;
      return {
        tableKey: payload.table_key,
        fixHeader,
        frozenLeft,
      };
    } catch {
      return { tableKey: payload.table_key, fixHeader: false, frozenLeft: [] };
    }
  }
);

const tableConfigurationSlice = createSlice({
  name: 'tableConfiguration',
  initialState,
  reducers: {
    clearTableConfig(state, { payload }) {
      if (payload && payload.tableKey) {
        delete state.configs[payload.tableKey];
      } else {
        state.configs = {};
      }
      state.status = false;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTableConfiguration.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchTableConfiguration.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        const { tableKey, fixHeader, frozenLeft } = action.payload;
        state.configs[tableKey] = { fixHeader, frozenLeft };
      })
      .addCase(fetchTableConfiguration.rejected, (state, action) => {
        if (action.payload === 'table configuration already fetched') {
          state.status = 'succeeded';
        } else {
          state.status = 'failed';
          state.error = action.error?.message || 'Fetch failed';
        }
      })
      .addCase(saveTableConfigurationThunk.pending, state => {
        state.saveLoading = true;
        state.saveError = null;
      })
      .addCase(saveTableConfigurationThunk.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.saveError = null;
        const { tableKey, fixHeader, frozenLeft } = action.payload;
        state.configs[tableKey] = { fixHeader, frozenLeft };
      })
      .addCase(saveTableConfigurationThunk.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveError =
          action.error?.message || action.payload || 'Save failed';
      });
  },
});

export const { clearTableConfig } = tableConfigurationSlice.actions;

export const tableConfigurationReducer = tableConfigurationSlice.reducer;
